import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Trash2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Overline = ({ children, className = '' }) => (
  <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] text-[#71717a] ${className}`}>{children}</p>
);

const pad = (n) => String(n).padStart(2, '0');

const useCountdown = (dateStr, active) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  if (!active || !dateStr) return null;
  const diff = new Date(dateStr).getTime() - now;
  if (Number.isNaN(diff)) return null;
  if (diff <= 0) return { due: true, label: 'Drawing soon' };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const sec = Math.floor((diff % 60000) / 1000);
  const label = d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m` : h > 0 ? `${h}h ${pad(m)}m ${pad(sec)}s` : `${m}m ${pad(sec)}s`;
  return { due: false, soon: diff < 3600000, label };
};

const Countdown = ({ draw }) => {
  const active = draw?.status === 'pending';
  const cd = useCountdown(draw?.draw_date, active);
  if (!cd) return null;
  const hot = cd.due || cd.soon;
  return (
    <div
      data-testid="draw-countdown"
      className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold tabular ${
        hot ? 'animate-pulse bg-[#EAB308]/20 text-[#EAB308]' : 'bg-white/[0.06] text-[#d4d4d8]'
      }`}
    >
      <Clock className="h-3 w-3" />
      {cd.due ? cd.label : <>Draws in <span className="text-white">{cd.label}</span></>}
    </div>
  );
};

const statusFor = (ticket, isWinner, isDeleted, isCompleted) => {
  if (isWinner) return { label: isDeleted ? 'Prize claimed' : 'Winner', cls: 'bg-[#EAB308] text-black', icon: true };
  if (isDeleted) return { label: 'Draw finished', cls: 'bg-white/[0.08] text-[#a1a1aa]' };
  if (isCompleted) return { label: 'Draw finished', cls: 'bg-white/[0.08] text-[#a1a1aa]' };
  if (ticket.draw?.status === 'countdown' || ticket.draw?.status === 'spinning') return { label: 'Live now', cls: 'bg-[#f43f5e] text-white animate-pulse' };
  return { label: 'Entered', cls: 'bg-[#A855F7]/20 text-[#c084fc]' };
};

const TicketCard = ({ ticket, delay = 0, onDelete }) => {
  const isWinner = ticket.draw?.winner_id === ticket.user_id && (ticket.draw?.status === 'completed' || ticket.draw?.status === 'deleted');
  const isCompleted = ticket.draw?.status === 'completed';
  const isDeleted = ticket.draw?.status === 'deleted' || ticket.draw_deleted;
  const status = statusFor(ticket, isWinner, isDeleted, isCompleted);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this ticket from your list?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/user/tickets/${ticket.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ticket removed');
      onDelete?.(ticket.id);
    } catch (error) {
      toast.error('Failed to remove ticket');
    }
  };

  const drawDate = ticket.draw?.draw_date
    ? new Date(ticket.draw.draw_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 120, damping: 18, delay }}
      data-testid={`ticket-card-${ticket.id}`}
      className={`relative overflow-hidden rounded-2xl border bg-[#18181b] ${
        isWinner
          ? 'border-[#EAB308]/60 shadow-[0_0_32px_-8px_rgba(234,179,8,0.45)]'
          : isDeleted
          ? 'border-white/[0.06] opacity-70'
          : 'border-white/[0.06]'
      }`}
    >
      {isWinner && <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#EAB308]/10 via-transparent to-transparent" />}

      <div className="relative p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Overline>Raffle ticket · {drawDate}</Overline>
            <h3 className="mt-1 break-words font-heading text-[15px] font-semibold leading-snug text-white sm:text-base">
              {ticket.draw?.title}
            </h3>
            <Countdown draw={ticket.draw} />
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${status.cls}`}>
              {status.icon && <Crown className="h-3 w-3 fill-black" />}
              {status.label}
            </span>
            <button
              onClick={handleDelete}
              data-testid={`delete-ticket-btn-${ticket.id}`}
              aria-label="Remove ticket"
              className="tap-none flex h-7 w-7 items-center justify-center rounded-full text-[#52525b] transition-colors hover:bg-[#f43f5e]/15 hover:text-[#f43f5e]"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {ticket.draw?.description && (
          <p className="mt-2 line-clamp-2 break-words text-sm leading-relaxed text-[#a1a1aa]">{ticket.draw.description}</p>
        )}
      </div>

      <div className="relative mx-0 border-t border-dashed border-white/[0.12]">
        <span className="absolute -left-2.5 -top-2.5 h-5 w-5 rounded-full border-r border-white/[0.08] bg-[#09090b]" />
        <span className="absolute -right-2.5 -top-2.5 h-5 w-5 rounded-full border-l border-white/[0.08] bg-[#09090b]" />
      </div>

      <div className="relative flex items-end justify-between gap-4 p-4 sm:p-5">
        <div className="min-w-0">
          <Overline>Your numbers</Overline>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {ticket.ticket_numbers.map((number) => {
              const winning = isWinner && number === ticket.draw?.winning_ticket;
              return (
                <span
                  key={number}
                  className={`tabular rounded-lg px-2.5 py-1 font-heading text-sm font-semibold ${
                    winning ? 'bg-[#EAB308] text-black shadow-[0_0_14px_rgba(234,179,8,0.5)]' : 'bg-white/[0.06] text-white'
                  }`}
                >
                  {number}
                </span>
              );
            })}
          </div>
        </div>
        <div className="max-w-[45%] shrink-0 text-right">
          <Overline>Prize</Overline>
          <p className="mt-1 break-words font-heading text-sm font-semibold leading-tight text-[#EAB308]">
            {ticket.draw?.prize_description}
            {ticket.draw?.bonus_amount > 0 && (
              <span className="ml-1 text-[#39FF14]" style={{ textShadow: '0 0 10px rgba(57,255,20,0.5)' }}>
                +£{ticket.draw.bonus_amount}
              </span>
            )}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default TicketCard;
